/**
 * @file ColorDialogSample.tsx
 * @description Образец «Аа Пример текста» в окне «Свой цвет» (phase19.16,
 * партия SB120; uiux-sb120-fields §3) с контрастом ✓/!/✕:
 *  - text — выбранный цвет текста на фоне элемента (нет фона — фон страницы);
 *  - fill — текст, который платформа подберёт по тону, на выбранной заливке.
 */

import { SITE_COLOR_PATTERN, SITE_CONTRAST_LEVEL_MARKS, type SiteColors } from "@chaiberi/shared";

import { contrastView, fillTextColors, opaqueBg, textContrast } from "../../lib/site-fields/color-contrast";

interface Props {
  mode: "text" | "fill";
  value: string;
  colors: SiteColors;
  /** Фон под текстом в режиме text. */
  bg?: string;
}

/** Пара «фон — текст» образца; `null` — цвет ещё не введён целиком. */
function samplePair({ mode, value, colors, bg }: Props): { background: string; text: string } | null {
  if (!SITE_COLOR_PATTERN.test(value)) return null;
  if (mode === "fill") return fillTextColors({ kind: "custom", color: value }, colors);
  const background = opaqueBg(bg ?? colors.pageBg, colors);
  return background ? { background, text: value } : null;
}

export function ColorDialogSample(props: Props) {
  const pair = samplePair(props);
  if (!pair) return <p className="type-caption text-on-surface-variant">Введите цвет в формате #RRGGBB.</p>;
  const ratio = textContrast(pair.text, pair.background, props.colors);
  const view = ratio === null ? null : contrastView(ratio);
  return (
    <div className="flex flex-col gap-1">
      <div
        aria-label="Образец текста"
        className="flex items-baseline gap-3 rounded-xl px-4 py-3 [box-shadow:inset_0_0_0_1px_var(--border)]"
        style={{ background: pair.background, color: pair.text }}
      >
        <span className="text-xl font-medium">Аа</span>
        <span className="text-base">Пример текста</span>
      </div>
      {view ? (
        <p className="type-caption text-on-surface">
          {props.mode === "fill" ? "Текст на этом цвете" : "Контраст с фоном"} — {view.label} {SITE_CONTRAST_LEVEL_MARKS[view.level]}
        </p>
      ) : null}
    </div>
  );
}
